<template>
  <section class="stock-chart-lookup" :class="{ compact }" aria-label="个股日K查询">
    <details class="stock-chart-lookup-disclosure">
    <summary class="stock-chart-lookup-label"><strong>{{ compact ? '日K查询' : '查询个股日K' }} <span aria-hidden="true">↗</span></strong><span v-if="!compact">名称 / 代码 · MA均线</span></summary>
    <div class="stock-chart-search" @focusout="onSearchBlur">
      <form class="stock-chart-search-form" @submit.prevent="submitSearch">
        <input
          ref="searchInput"
          v-model.trim="query"
          type="search"
          role="combobox"
          aria-label="输入股票代码或名称查询日K"
          aria-autocomplete="list"
          aria-controls="stock-chart-suggestions"
          :aria-expanded="showSuggestions && suggestions.length > 0 ? 'true' : 'false'"
          :aria-activedescendant="suggestionIndex >= 0 ? `stock-chart-option-${suggestionIndex}` : null"
          placeholder="如 603200 / 上海洗霸"
          autocomplete="off"
          @input="onSearchInput"
          @focus="showSuggestions = true"
          @keydown.down.prevent="moveSuggestion(1)"
          @keydown.up.prevent="moveSuggestion(-1)"
          @keydown.esc.stop="showSuggestions = false"
        >
        <button type="submit">查看日K ↗</button>
      </form>
      <ul v-if="showSuggestions && suggestions.length" id="stock-chart-suggestions" class="stock-chart-suggestions" role="listbox">
        <li v-for="(item, index) in suggestions" :id="`stock-chart-option-${index}`" :key="item.code" role="option" :aria-selected="suggestionIndex === index ? 'true' : 'false'">
          <button type="button" :class="{ active: suggestionIndex === index }" @mousedown.prevent @click="chooseSuggestion(item)">
            <strong>{{ item.name || item.code }}</strong><code>{{ item.code }}</code><small>{{ item.industry || item.market }}</small>
          </button>
        </li>
      </ul>
      <p v-if="searchError || searchLoading" class="stock-chart-search-note" role="status">{{ searchLoading ? '正在查找…' : searchError }}</p>
    </div>
    </details>
    <div v-if="isOpen" class="stock-chart-overlay" @click.self="close">
      <section ref="dialog" class="stock-chart-dialog" role="dialog" aria-modal="true" aria-labelledby="stock-chart-title" tabindex="-1" @keydown="onDialogKeydown">
        <header class="stock-chart-header">
          <div><p>DAILY / 日线</p><h3 id="stock-chart-title">{{ stock.name || stock.code }} <code>{{ stock.code }}</code></h3></div>
          <button ref="closeButton" class="stock-chart-close" type="button" aria-label="关闭日K图" @click="close">×</button>
        </header>
        <div v-if="loading" class="stock-chart-message" role="status">正在读取日K和均线…</div>
        <div v-else-if="error" class="stock-chart-message" role="alert">{{ error }} <button type="button" @click="openStock(stock)">重试</button></div>
        <template v-else-if="bars.length">
          <div class="stock-chart-toolbar">
            <span>{{ activeBar.trade_date }} <small>{{ activeIndex < 0 ? '最近日线' : '历史日线' }}</small></span>
            <div class="stock-chart-ranges" aria-label="日K显示范围">
              <button v-for="count in [60, 120, 250]" :key="count" type="button" :class="{ active: visibleCount === count }" :aria-pressed="visibleCount === count ? 'true' : 'false'" @click="setRange(count)">近{{ count }}日</button>
            </div>
          </div>
          <div class="stock-chart-ohlc">
            <span>开 <b>{{ price(activeBar.open) }}</b></span><span>高 <b>{{ price(activeBar.high) }}</b></span>
            <span>低 <b>{{ price(activeBar.low) }}</b></span><span>收 <b :class="activeBar.close >= activeBar.open ? 'stock-chart-up' : 'stock-chart-down'">{{ price(activeBar.close) }}</b></span>
            <span>涨跌 <b :class="Number(activeBar.change_pct) >= 0 ? 'stock-chart-up' : 'stock-chart-down'">{{ percent(activeBar.change_pct) }}</b></span>
          </div>
          <div class="stock-chart-ma-values" aria-label="所选日期的均线数值" aria-live="polite">
            <div v-for="ma in maStyles" :key="ma.period" :style="{ '--ma-color': ma.color }">
              <span>MA{{ ma.period }}</span><strong>{{ price(activeBar[`ma${ma.period}`]) }}</strong>
            </div>
          </div>
          <details v-if="result.bottom_repair && result.bottom_repair.enabled" class="stock-repair-detail">
            <summary><strong>筑底修复 · {{ activeRepair.stage_label || '数据不足' }}</strong><span>{{ activeRepair.evidence_count === null || activeRepair.evidence_count === undefined ? '--' : activeRepair.evidence_count }}/4 项证据</span><small>{{ result.bottom_repair.freshness === 'stale' ? '历史日线，等待补齐' : '已完成日线' }}</small></summary>
            <div class="stock-repair-values">
              <div><span>地量锚点</span><strong>{{ activeRepair.anchor_date || '--' }}</strong></div>
              <div><span>锚定VWAP</span><strong>{{ price(activeRepair.avwap) }}</strong></div>
              <div><span>0.5扇线</span><strong>{{ price(activeRepair.fan_05) }}</strong></div>
              <div><span>参考失效价</span><strong>{{ price(activeRepair.reference_stop) }}</strong></div>
              <div><span>风险距离 / 量能倍数</span><strong>{{ percent(activeRepair.risk_distance_pct) }} / {{ price(activeRepair.volume_multiple_5) }}倍</strong></div>
            </div>
            <ul class="stock-repair-evidence">
              <li v-for="item in activeRepair.evidence || []" :key="item.id"><strong>{{ item.label }}</strong><span>{{ item.status === 'passed' ? '满足' : item.status === 'unavailable' ? '数据不足 / 待端点确认' : '待满足' }}</span><small>{{ item.date || '--' }}</small></li>
            </ul>
            <p v-if="activeRepair.divergence">背离低点 {{ activeRepair.divergence.first_date }} → {{ activeRepair.divergence.second_date }} · {{ activeRepair.divergence.confirmed_at }} 才确认</p>
            <p>本地存储 {{ result.bottom_repair.stored_history_count === undefined ? '--' : result.bottom_repair.stored_history_count }} 根 · 所选日指标连续 {{ activeRepair.history_segment_count === undefined ? '--' : activeRepair.history_segment_count }} 根 / 至少 {{ activeRepair.history_required_count || 60 }} 根</p>
            <p v-if="activeRepair.last_reset">最近分段 {{ activeRepair.last_reset.trade_date || '--' }} · {{ resetLabel(activeRepair.last_reset.reason) }}<span v-if="activeRepair.last_reset.previous_source">（{{ activeRepair.last_reset.previous_source }} → {{ activeRepair.last_reset.source }}）</span></p>
            <p>严格版：放量≥前5日均量{{ result.bottom_repair.parameters.volume_multiple }}倍；止损参考为结构低点下方{{ result.bottom_repair.parameters.stop_buffer_pct }}%，并非买价下方3%。</p>
            <p v-for="issue in result.bottom_repair.issues || []" :key="issue">{{ issue }}</p>
          </details>
          <div v-if="result.bottom_repair && result.bottom_repair.enabled" class="stock-repair-toggle">
            <button type="button" :aria-pressed="showRepair ? 'true' : 'false'" @click="showRepair = !showRepair">{{ showRepair ? '隐藏' : '显示' }}修复参考线</button>
            <small>AVWAP / 0.5扇线 / 失效价 · 随所选日期读取</small>
          </div>
          <div class="stock-chart-scroll" tabindex="0" aria-label="日K图，左右方向键查看各日价格与均线" @keydown.left.prevent="moveBar(-1)" @keydown.right.prevent="moveBar(1)" @keydown.home.prevent="activeIndex = 0" @keydown.end.prevent="activeIndex = -1">
            <svg class="stock-chart-svg" :viewBox="`0 0 ${geometry.width} ${geometry.height}`" role="img" aria-labelledby="stock-chart-svg-title stock-chart-svg-desc" @mouseleave="activeIndex = -1">
              <title id="stock-chart-svg-title">{{ stock.name || stock.code }} 日K与MA5、10、13、17、55</title>
              <desc id="stock-chart-svg-desc">红涨绿跌，下方为成交量。移动鼠标或点击K线查看当日价格和均线，也可用左右方向键逐日查看。</desc>
              <g v-for="tick in geometry.ticks" :key="tick.y" class="stock-chart-grid">
                <line :x1="geometry.left" :x2="geometry.right" :y1="tick.y" :y2="tick.y" />
                <text :x="geometry.left - 10" :y="tick.y + 4" text-anchor="end">{{ price(tick.value) }}</text>
              </g>
              <text :x="geometry.left" :y="geometry.volumeTop - 8" class="stock-chart-axis">成交量（相对高度）</text>
              <g v-for="candle in geometry.candles" :key="candle.date" :fill="candle.color" :stroke="candle.color">
                <line :x1="candle.x" :x2="candle.x" :y1="candle.high" :y2="candle.low" />
                <rect :x="candle.x - geometry.candleWidth / 2" :y="candle.bodyY" :width="geometry.candleWidth" :height="candle.bodyHeight" stroke="none" />
                <rect :x="candle.x - geometry.candleWidth / 2" :y="candle.volumeY" :width="geometry.candleWidth" :height="candle.volumeHeight" opacity=".45" stroke="none" />
              </g>
              <g v-for="line in geometry.maLines" :key="line.period" fill="none" :stroke="line.color" stroke-width="1.6" stroke-linejoin="round">
                <polyline v-for="(points, index) in line.segments" :key="index" :points="points" />
              </g>
              <g v-for="line in geometry.repairLines" :key="line.key" fill="none" :stroke="line.color" stroke-width="1.8" stroke-dasharray="5 3">
                <polyline v-for="(points, index) in line.segments" :key="index" :points="points"><title>{{ line.label }}</title></polyline>
              </g>
              <g v-for="tick in geometry.dates" :key="tick.index" class="stock-chart-axis">
                <text :x="tick.x" :y="geometry.height - 10" text-anchor="middle">{{ tick.label }}</text>
              </g>
              <g v-if="geometry.candles.length && activeIndex >= 0" class="stock-chart-crosshair">
                <line :x1="geometry.candles[activeIndex].x" :x2="geometry.candles[activeIndex].x" :y1="geometry.top" :y2="geometry.volumeBottom" />
                <line :x1="geometry.left" :x2="geometry.right" :y1="geometry.candles[activeIndex].close" :y2="geometry.candles[activeIndex].close" />
              </g>
              <rect v-for="(candle, index) in geometry.candles" :key="`hit-${candle.date}`" :x="candle.x - geometry.step / 2" :y="geometry.top" :width="geometry.step" :height="geometry.volumeBottom - geometry.top" fill="transparent" @mouseenter="activeIndex = index" @click="activeIndex = index" />
            </svg>
          </div>
          <footer class="stock-chart-footer">
            <span>移动 / 点击K线查看当日MA值 · 共{{ displayBars.length }}根日K · MA为收盘价简单平均</span>
            <span>本地日线 · 截至 {{ bars[bars.length - 1].trade_date }} · {{ result.price_basis || '沿用本地入库价格口径' }}</span>
            <span v-if="result.message">{{ result.message }}</span>
            <span>历史不足时均线显示 --；除权除息前后请核对复权口径。</span>
          </footer>
        </template>
        <div v-else class="stock-chart-message">{{ result.message || '本地暂无该股票日线，请先完成日线采集入库。' }}</div>
      </section>
    </div>
  </section>
</template>

<script>
const API_BASE = (process.env.VUE_APP_BASE_API || '').replace(/\/$/, '')
const MA_STYLES = [
  { period: 5, color: '#a36914' }, { period: 10, color: '#a74779' },
  { period: 13, color: '#287eab' }, { period: 17, color: '#63852a' },
  { period: 55, color: '#7855b2' }
]
const REPAIR_STYLES = [
  { key: 'avwap', color: '#147d76', label: '锚定VWAP' },
  { key: 'fan_05', color: '#cf7b19', label: '速度阻力扇0.5' },
  { key: 'reference_stop', color: '#8b6666', label: '参考失效价' }
]

export default {
  props: { compact: { type: Boolean, default: false } },
  name: 'StockDailyChart',
  data () {
    return {
      query: '', suggestions: [], suggestionIndex: -1, showSuggestions: false,
      searchLoading: false, searchError: '', searchRequestId: 0,
      searchTimer: null, searchController: null,
      isOpen: false, stock: {}, loading: false, error: '', result: {}, bars: [],
      klineRequestId: 0, klineController: null, visibleCount: 120, activeIndex: -1,
      returnFocus: null, showRepair: true
    }
  },
  computed: {
    maStyles () { return MA_STYLES },
    displayBars () { return this.bars.slice(-this.visibleCount) },
    activeBar () { return this.displayBars[this.activeIndex] || this.displayBars[this.displayBars.length - 1] || {} },
    activeRepair () { return this.activeBar.bottom_repair || {} },
    geometry () {
      const width = 1000; const height = 410
      const left = 68; const right = 976; const top = 18; const priceBottom = 282
      const volumeTop = 316; const volumeBottom = 378
      const bars = this.displayBars
      const valid = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) && Number(value) > 0
      const prices = bars.flatMap(bar => [bar.low, bar.high, ...MA_STYLES.map(ma => bar[`ma${ma.period}`]),
        ...(this.showRepair ? REPAIR_STYLES.map(line => (bar.bottom_repair || {})[line.key]) : [])]).filter(valid).map(Number)
      const low = prices.length ? Math.min(...prices) : 0
      const high = prices.length ? Math.max(...prices) : 1
      const padding = Math.max((high - low) * 0.07, high * 0.003, 0.01)
      const min = Math.max(0, low - padding); const max = high + padding
      const y = value => top + (max - Number(value)) * (priceBottom - top) / (max - min)
      const step = (right - left) / Math.max(bars.length, 1)
      const x = index => left + step * (index + 0.5)
      const maxVolume = Math.max(...bars.map(bar => Number(bar.volume) || 0), 1)
      const candles = bars.map((bar, index) => {
        const open = y(bar.open); const close = y(bar.close)
        const volumeHeight = Math.max(0, Number(bar.volume) || 0) / maxVolume * (volumeBottom - volumeTop)
        return { date: bar.trade_date, x: x(index), high: y(bar.high), low: y(bar.low), close,
          bodyY: Math.min(open, close), bodyHeight: Math.max(Math.abs(open - close), 1),
          volumeY: volumeBottom - volumeHeight, volumeHeight, color: Number(bar.close) >= Number(bar.open) ? '#c34e47' : '#1d8b63' }
      })
      const maLines = MA_STYLES.map(ma => {
        const segments = []; let segment = []
        bars.forEach((bar, index) => {
          const value = bar[`ma${ma.period}`]
          if (valid(value)) segment.push(`${x(index)},${y(value)}`)
          else if (segment.length) { segments.push(segment.join(' ')); segment = [] }
        })
        if (segment.length) segments.push(segment.join(' '))
        return { ...ma, segments }
      })
      const ticks = Array.from({ length: 5 }, (_, index) => {
        const value = max - (max - min) * index / 4
        return { value, y: y(value) }
      })
      const dates = [...new Set([0, Math.floor((bars.length - 1) / 3), Math.floor((bars.length - 1) * 2 / 3), bars.length - 1])]
        .filter(index => index >= 0 && bars[index]).map(index => ({ index, x: x(index), label: bars[index].trade_date }))
      const repairLines = this.showRepair ? REPAIR_STYLES.map(line => {
        const segments = []; let segment = []; let previousStructure = null
        bars.forEach((bar, index) => {
          const repair = bar.bottom_repair || {}
          const structure = `${repair.structure_id || ''}:${line.key === 'fan_05' ? repair.fan_low_date || '' : ''}`
          if (structure !== previousStructure || !valid(repair[line.key])) {
            if (segment.length) segments.push(segment.join(' '))
            segment = []
          }
          if (valid(repair[line.key])) segment.push(`${x(index)},${y(repair[line.key])}`)
          previousStructure = structure
        })
        if (segment.length) segments.push(segment.join(' '))
        return { ...line, segments }
      }) : []
      return { width, height, left, right, top, volumeTop, volumeBottom, step, candleWidth: Math.max(1, Math.min(step * 0.65, 11)), candles, maLines, repairLines, ticks, dates }
    }
  },
  watch: {
    isOpen (open) { document.body.classList.toggle('stock-chart-open', open) }
  },
  beforeDestroy () {
    document.body.classList.remove('stock-chart-open')
    this.cancelSearch()
    this.cancelKline()
  },
  methods: {
    resetLabel (reason) { return ({ price_basis_changed: '复权口径不同或未注明', unverified_source_transition: '来源价量口径尚未核验', raw_reference_discontinuity: '前收盘参考价不连续，需核对除权除息', missing_session: '交易日日线缺口', invalid_price_or_volume: '日线价量缺失或无效' })[reason] || '历史已分段' },
    responseError (payload, fallback) {
      const detail = payload && payload.detail
      return (typeof detail === 'string' ? detail : detail && detail.message) || (payload && payload.message) || fallback
    },
    async responseJson (response, fallback) {
      // Read the body once so a legacy/incorrect proxy response such as
      // `404 page not found` does not surface as a JSON parser exception.
      if (!response || typeof response.text !== 'function') return response.json()
      const text = await response.text()
      try {
        return text.trim() ? JSON.parse(text) : {}
      } catch (error) {
        const status = response.status ? `HTTP ${response.status}` : 'HTTP未知'
        const detail = response.status === 404
          ? '接口不存在，请检查 Nginx 的 /api/v1/stocks/ 转发及看板后端版本'
          : '接口返回了非 JSON 内容，请检查反向代理配置'
        throw new Error(`${fallback}（${status}）：${detail}`)
      }
    },
    price (value) {
      return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '--'
    },
    percent (value) {
      if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return '--'
      return `${Number(value) > 0 ? '+' : ''}${Number(value).toFixed(2)}%`
    },
    codeFromInput (value) {
      const match = String(value || '').trim().toUpperCase().match(/^(?:(?:SH|SZ|BJ)\s*)?(\d{6})(?:\.(?:SH|SZ|BJ))?$/)
      return match ? match[1] : ''
    },
    cancelSearch () {
      this.searchRequestId += 1
      if (this.searchTimer) window.clearTimeout(this.searchTimer)
      if (this.searchController) this.searchController.abort()
      this.searchController = null; this.searchTimer = null; this.searchLoading = false
    },
    onSearchInput () {
      this.cancelSearch()
      this.searchError = ''; this.suggestions = []; this.suggestionIndex = -1; this.showSuggestions = true
      if (!this.query) return
      this.searchTimer = window.setTimeout(() => { this.searchTimer = null; this.fetchSuggestions() }, 240)
    },
    async fetchSuggestions () {
      this.cancelSearch()
      const query = this.query.trim()
      if (!query) return []
      const requestId = this.searchRequestId
      const controller = new window.AbortController()
      this.searchController = controller; this.searchLoading = true; this.searchError = ''
      const timeout = window.setTimeout(() => controller.abort(), 10000)
      try {
        const response = await fetch(`${API_BASE}/api/v1/stocks/search?q=${encodeURIComponent(query)}&limit=10`, { signal: controller.signal, headers: { Accept: 'application/json' } })
        const payload = await this.responseJson(response, '股票检索失败')
        if (response.status === 404) throw new Error('股票检索接口不存在，请检查 Nginx 的 /api/v1/stocks/ 转发及看板后端版本')
        if (!response.ok) throw new Error(this.responseError(payload, '股票检索暂不可用'))
        if (requestId !== this.searchRequestId) return []
        this.suggestions = Array.isArray(payload.items) ? payload.items : []
        this.suggestionIndex = -1
        if (!this.suggestions.length) this.searchError = '未找到匹配股票，可直接输入六位代码查询已入库日K。'
        return this.suggestions
      } catch (error) {
        if (requestId === this.searchRequestId) this.searchError = error.name === 'AbortError' ? '股票检索超时，请重试。' : error.message
        return []
      } finally {
        window.clearTimeout(timeout)
        if (requestId === this.searchRequestId) { this.searchLoading = false; this.searchController = null }
      }
    },
    moveSuggestion (direction) {
      this.showSuggestions = true
      const count = this.suggestions.length
      if (count) this.suggestionIndex = this.suggestionIndex < 0
        ? (direction > 0 ? 0 : count - 1)
        : (this.suggestionIndex + direction + count) % count
    },
    onSearchBlur (event) {
      if (!event.currentTarget.contains(event.relatedTarget)) this.showSuggestions = false
    },
    chooseSuggestion (stock) {
      this.cancelSearch(); this.showSuggestions = false
      this.query = stock.name || stock.code
      return this.openStock(stock)
    },
    async submitSearch () {
      const code = this.codeFromInput(this.query)
      if (code) return this.chooseSuggestion(this.suggestions.find(item => item.code === code) || { code })
      if (this.suggestionIndex >= 0 && this.suggestions[this.suggestionIndex]) return this.chooseSuggestion(this.suggestions[this.suggestionIndex])
      const query = this.query.trim()
      if (!query) { this.searchError = '请输入股票代码或名称。'; return }
      const items = await this.fetchSuggestions()
      if (this.query.trim() !== query) return
      const exact = items.filter(item => item.name === query)
      if (exact.length === 1 || items.length === 1) return this.chooseSuggestion(exact[0] || items[0])
      this.showSuggestions = true
      if (items.length > 1) this.searchError = '匹配到多只股票，请从联想列表选择。'
    },
    cancelKline () {
      this.klineRequestId += 1
      if (this.klineController) this.klineController.abort()
      this.klineController = null
    },
    async openStock (stock) {
      const code = this.codeFromInput(stock && stock.code)
      if (!code) { this.searchError = '请输入六位股票代码或选择联想结果。'; return }
      if (!this.isOpen) this.returnFocus = document.activeElement
      this.cancelSearch(); this.showSuggestions = false; this.cancelKline()
      const requestId = this.klineRequestId
      const controller = new window.AbortController()
      this.klineController = controller
      this.stock = { code, name: stock.name || '' }; this.isOpen = true; this.loading = true
      this.error = ''; this.bars = []; this.result = {}; this.activeIndex = -1
      this.$nextTick(() => { if (this.isOpen && this.$refs.closeButton) this.$refs.closeButton.focus() })
      const timeout = window.setTimeout(() => controller.abort(), 15000)
      try {
        const response = await fetch(`${API_BASE}/api/v1/stocks/${encodeURIComponent(code)}/daily-kline?limit=250`, { signal: controller.signal, headers: { Accept: 'application/json' } })
        const payload = await this.responseJson(response, '日K读取失败')
        if (response.status === 404) throw new Error('日K接口不存在，请检查 Nginx 的 /api/v1/stocks/ 转发及看板后端版本')
        if (!response.ok) throw new Error(this.responseError(payload, '日K读取失败'))
        if (requestId !== this.klineRequestId) return
        this.result = payload
        this.bars = Array.isArray(payload.bars) ? payload.bars : []
        this.stock = { code, name: payload.name || stock.name || '' }
      } catch (error) {
        if (requestId === this.klineRequestId) this.error = error.name === 'AbortError' ? '日K查询超时，请重试。' : error.message
      } finally {
        window.clearTimeout(timeout)
        if (requestId === this.klineRequestId) { this.loading = false; this.klineController = null }
      }
    },
    close () {
      this.cancelKline(); this.isOpen = false; this.loading = false
      this.$nextTick(() => { if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus() })
    },
    onDialogKeydown (event) {
      if (event.key === 'Escape') { event.stopPropagation(); this.close(); return }
      if (event.key !== 'Tab') return
      const nodes = [...this.$refs.dialog.querySelectorAll('button:not([disabled]), [tabindex="0"]')]
      const first = nodes[0]; const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    },
    setRange (count) { this.visibleCount = count; this.activeIndex = -1 },
    moveBar (direction) {
      const length = this.displayBars.length
      if (!length) return
      const current = this.activeIndex < 0 ? length - 1 : this.activeIndex
      this.activeIndex = Math.max(0, Math.min(length - 1, current + direction))
    }
  }
}
</script>

<style scoped>
.stock-chart-lookup { margin-bottom: 14px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 8px; }
.stock-chart-lookup-label { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 44px; padding: 10px 14px; cursor: pointer; font-size: 12px; list-style: none; }
.stock-chart-lookup-label::-webkit-details-marker { display: none; }
.stock-chart-lookup-disclosure[open] .stock-chart-lookup-label { border-bottom: 1px solid var(--hairline); }
.stock-chart-lookup-label span { color: var(--muted); font-size: 11px; }
.stock-chart-search { position: relative; width: min(calc(100% - 28px), 540px); margin: 12px 14px; }
.stock-chart-lookup.compact { flex: 0 0 auto; margin: 0; }
.compact .stock-chart-lookup-label { min-height: 38px; padding: 8px 11px; font-size: 12px; }
.compact .stock-chart-search { position: absolute; z-index: 25; top: calc(100% + 8px); left: 0; width: 100%; margin: 0; padding: 12px; background: var(--paper); border: 1px solid var(--hairline); border-radius: 8px; box-shadow: var(--shadow); }
.compact .stock-chart-suggestions { right: 12px; left: 12px; }
.stock-chart-search-form { display: flex; border: 1px solid var(--hairline); border-radius: 5px; overflow: hidden; }
.stock-chart-search-form input { min-width: 0; flex: 1; padding: 9px 11px; border: none; outline: none; color: var(--ink); background: transparent; font-size: 12px; }
.stock-chart-search-form:focus-within { border-color: var(--coral); }
.stock-chart-search-form button { padding: 9px 12px; border: none; background: var(--dark); color: #fff; font-size: 12px; white-space: nowrap; }
.stock-chart-suggestions { position: absolute; z-index: 12; top: 100%; left: 0; right: 0; margin: 4px 0 0; padding: 4px; list-style: none; background: var(--paper); border: 1px solid var(--hairline); box-shadow: var(--shadow); border-radius: 5px; }
.stock-chart-suggestions button { display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px; text-align: left; border: none; border-radius: 3px; background: transparent; color: var(--ink); }
.stock-chart-suggestions button:hover, .stock-chart-suggestions button.active { background: var(--cream); }
.stock-chart-suggestions strong { font-size: 12px; }
.stock-chart-suggestions code, .stock-chart-suggestions small { color: var(--muted); font-size: 11px; }
.stock-chart-suggestions small { margin-left: auto; max-width: 40%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.stock-chart-search-note { margin: 6px 0 0; font-size: 11px; color: var(--coral); }
.stock-chart-overlay { position: fixed; z-index: 150; inset: 0; display: grid; place-items: center; padding: 24px; overflow-y: auto; background: rgba(14, 23, 34, .62); overscroll-behavior: contain; }
.stock-chart-dialog { width: min(1100px, 100%); max-height: calc(100dvh - 48px); padding: 24px 28px 20px; overflow-y: auto; border: 1px solid var(--hairline); border-radius: 12px; background: var(--paper); box-shadow: 0 24px 90px rgba(0,0,0,.24); }
.stock-chart-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
.stock-chart-header p { margin: 0 0 6px; font-size: 10px; letter-spacing: .12em; color: var(--coral); }
.stock-chart-header h3 { margin: 0; font-size: 24px; font-weight: 600; }
.stock-chart-header code { margin-left: 8px; font-size: 14px; font-weight: 400; color: var(--muted); }
.stock-chart-close { width: 34px; height: 34px; flex-shrink: 0; border: 1px solid var(--hairline); border-radius: 50%; color: var(--ink); background: transparent; font-size: 25px; line-height: 1; }
.stock-chart-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 14px; font-size: 13px; }
.stock-chart-toolbar small { color: var(--muted); margin-left: 8px; font-size: 11px; }
.stock-chart-ranges { display: flex; gap: 4px; }
.stock-chart-ranges button { padding: 5px 9px; border: 1px solid var(--hairline); border-radius: 4px; background: transparent; color: var(--muted); font-size: 11px; }
.stock-chart-ranges button.active { background: var(--dark); color: #fff; border-color: var(--dark); }
.stock-chart-ohlc { display: flex; flex-wrap: wrap; gap: 8px 22px; font-size: 12px; color: var(--muted); }
.stock-chart-ohlc b { margin-left: 5px; font-variant-numeric: tabular-nums; }
.stock-chart-up { color: #c34e47; }.stock-chart-down { color: #1d8b63; }
.stock-chart-ma-values { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin: 16px 0; }
.stock-chart-ma-values div { padding: 9px 12px; border: 1px solid var(--hairline); border-top: 2px solid var(--ma-color); border-radius: 4px; color: var(--ma-color); }
.stock-chart-ma-values span { display: block; font-size: 11px; }
.stock-chart-ma-values strong { display: block; margin-top: 4px; font-size: 17px; font-weight: 500; font-variant-numeric: tabular-nums; }
.stock-chart-scroll { overflow-x: auto; border: 1px solid var(--hairline); border-radius: 5px; }
.stock-chart-svg { display: block; width: 100%; min-width: 640px; }
.stock-chart-grid line { stroke: var(--hairline); stroke-dasharray: 3 4; stroke-width: .7; }
.stock-chart-grid text, .stock-chart-axis { fill: var(--muted); font-size: 10px; }
.stock-chart-crosshair { stroke: var(--muted); stroke-width: 1; stroke-dasharray: 4 4; pointer-events: none; }
.stock-repair-detail { margin-bottom: 12px; padding: 12px; border: 1px solid var(--hairline); border-radius: 5px; font-size: 11px; }
.stock-repair-detail summary { display: flex; flex-wrap: wrap; gap: 8px 14px; cursor: pointer; }
.stock-repair-detail small, .stock-repair-detail p { color: var(--muted); line-height: 1.6; overflow-wrap: anywhere; }
.stock-repair-values { display: grid; grid-template-columns: repeat(auto-fit,minmax(140px,1fr)); gap: 12px; margin-top: 16px; }
.stock-repair-values span, .stock-repair-values strong { display: block; margin-bottom: 5px; }
.stock-repair-values span { color: var(--muted); }
.stock-repair-evidence { display: grid; gap: 8px; margin: 14px 0; padding: 0; list-style: none; }
.stock-repair-evidence li { display: flex; gap: 12px; flex-wrap: wrap; }
.stock-repair-evidence small { margin-left: auto; }
.stock-repair-toggle { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 10px 0; color: var(--muted); }
.stock-repair-toggle button { min-height: 36px; padding: 6px 10px; color: var(--ink); background: transparent; border: 1px solid var(--hairline); border-radius: 4px; font-size: 11px; }
.stock-chart-footer { display: grid; gap: 5px; padding-top: 14px; color: var(--muted); font-size: 11px; line-height: 1.5; }
.stock-chart-message { min-height: 200px; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 12px; color: var(--muted); font-size: 13px; }
.stock-chart-message button { padding: 5px 12px; color: var(--coral); background: transparent; border: 1px solid var(--hairline); border-radius: 4px; }
@media (max-width: 700px) {
  .compact .stock-chart-lookup-label { min-height: 44px; }
  .stock-chart-search { width: calc(100% - 28px); }
  .stock-chart-search-form input { font-size: 16px; min-height: 44px; }
  .stock-chart-overlay { padding: 8px; }.stock-chart-dialog { padding: 16px 12px; max-height: calc(100dvh - 16px); }
  .stock-chart-header h3 { font-size: 21px; }.stock-chart-header code { font-size: 12px; }
  .stock-chart-toolbar { align-items: flex-start; flex-direction: column; gap: 10px; }
  .stock-chart-ma-values { gap: 4px; }.stock-chart-ma-values div { padding: 7px 5px; }.stock-chart-ma-values strong { font-size: 14px; }
  .stock-chart-ohlc { gap: 8px 12px; }
}
</style>
